import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Bell, Building2, LogOut, Menu, Plus } from 'lucide-react';
import { ROLE_LABELS, type RoleKey } from '@fpc/shared';
import { api } from '@/lib/api';
import { useMoney } from '@/hooks/useMoney';
import { useAuth } from '@/hooks/useAuth';
import { useLayout } from '@/hooks/useMediaQuery';
import { NAV_GROUPS, bottomNavItems, homePath, navItemVisible } from '@/lib/navigation';
import { CompanySwitcher } from './CompanySwitcher';
import { GlobalSearch } from './GlobalSearch';
import { DueReminderBanner } from './DueReminderBanner';
import { UploadInvoiceModal } from '@/pages/Invoices';

/** Survives a reload so the rail is a preference, not a per-page accident. */
const COLLAPSED_KEY = 'fpc.sidebar.collapsed';

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function Layout() {
  const {
    user,
    logout,
    can,
    canAny,
    companyId,
    setCompanyId,
    verticalId,
    setVerticalId,
    company,
    viewIn,
    setViewIn,
    inrRates,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Phone and tablet get a compact header, a bottom bar and the profile in the drawer.
  const compact = useLayout() !== 'desktop';
  const [menuOpen, setMenuOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const menuRef = useRef<HTMLDivElement>(null);

  const { data: companies } = useQuery({
    queryKey: ['companies'],
    queryFn: () => api.settings.companies({ pageSize: 100 }),
    enabled: !!user && can('company:read'),
  });

  // Only fetched when the user can see verticals at all, so the filter simply
  // does not exist for a tenant that has not set any up.
  const { data: verticals } = useQuery({
    queryKey: ['verticals', 'for-shell', companyId],
    queryFn: () => api.settings.verticals({ companyId, pageSize: 100 }),
    enabled: !!user && can('vertical:read'),
  });

  const { data: unread } = useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: () => api.notifications.unreadCount(),
    enabled: !!user && can('notification:read'),
    refetchInterval: 60_000,
  });

  // The same key the dashboard uses, so on /dashboard this costs no request at
  // all and elsewhere it is one cheap summary shared by every screen.
  const { data: summary } = useQuery({
    queryKey: ['dashboard', companyId, verticalId],
    queryFn: () => api.dashboard.summary({ companyId, verticalId }),
    enabled: !!user && can('dashboard:read'),
  });

  // A drawer that survived navigation would cover the page it just opened.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setNavOpen(false);
      setMenuOpen(false);
    };
    const onClick = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, []);

  const visibleGroups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => navItemVisible(item, canAny)),
  })).filter((group) => group.items.length > 0);

  const toggleCollapsed = () => {
    setCollapsed((value) => {
      const next = !value;
      try {
        window.localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        // A blocked storage quota is no reason to refuse the toggle.
      }
      return next;
    });
  };

  // The bottom bar's tabs: the first four the user's role can open.
  const bottomItems = bottomNavItems(
    visibleGroups.flatMap((group) => group.items),
    user?.roleKey,
  );
  const showBottomNav = compact && bottomItems.length > 1;

  const signOut = () => void logout().then(() => navigate('/login'));

  const notificationsLink = can('notification:read') ? (
    <NavLink
      to="/notifications"
      className="btn-icon shrink-0"
      aria-label={
        unread && unread.count > 0 ? `Notifications, ${unread.count} unread` : 'Notifications'
      }
    >
      <Bell className="h-5 w-5" aria-hidden="true" />
      {unread && unread.count > 0 ? (
        <span className="absolute -right-0.5 -top-0.5 min-w-[18px] rounded-full bg-red-600 px-1 text-[10px] font-semibold leading-[18px] text-white ring-2 ring-white">
          {unread.count > 99 ? '99+' : unread.count}
        </span>
      ) : null}
    </NavLink>
  ) : null;

  const switcher = companies && companies.items.length > 1 ? companies.items : null;
  // Offered only when there is a choice to make: one vertical is already the
  // whole view, and none means the tenant does not use them.
  const verticalChoices = verticals && verticals.items.length > 1 ? verticals.items : null;

  /**
   * `rail` is the collapsed, icon-only sidebar. It is a parameter rather than
   * the state itself because the mobile drawer renders the same markup and is
   * never a rail — a drawer you had to expand would be no drawer at all.
   */
  const renderSidebar = (rail: boolean, drawer = false) => (
    <>
      <div
        className={`flex shrink-0 items-center gap-3 border-b border-ink-100 py-4 ${
          rail ? 'flex-col px-2' : 'px-3'
        }`}
      >
        <NavLink to={homePath(canAny)} className="flex min-w-0 items-center gap-2">
          <img
            src="/elixir-mark.png"
            alt=""
            width={32}
            height={32}
            className="h-8 w-8 shrink-0"
            aria-hidden="true"
          />
          {rail ? (
            <span className="sr-only">Elixir Finance Ops</span>
          ) : (
            /* Sized to clear the rail toggle at `w-56` so neither line clips. */
            <span className="min-w-0">
              <span className="block truncate text-[13px] font-semibold leading-tight text-ink-900">
                Elixir Finance Ops
              </span>
              <span className="block truncate text-[11px] leading-tight text-ink-500">
                Money-out operations
              </span>
            </span>
          )}
        </NavLink>

        {/* The rail only exists on `lg`, so its toggle lives there too. */}
        <button
          type="button"
          className={`btn-icon hidden h-8 w-8 shrink-0 lg:inline-flex ${rail ? '' : 'ml-auto'}`}
          onClick={toggleCollapsed}
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <Menu className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      {/*
        In the phone/tablet drawer the profile sits under the product name,
        since the compact header keeps only the greeting and notifications.
      */}
      {drawer ? (
        <div className="shrink-0 border-b border-ink-100 px-3 py-3">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-100 text-sm font-semibold text-brand-800">
              {initials(user?.name)}
            </span>
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold text-ink-900">
                {user?.name}
              </span>
              <span className="block truncate text-xs text-ink-500">
                {user ? (ROLE_LABELS[user.roleKey as RoleKey] ?? user.roleKey) : null}
              </span>
            </span>
          </div>
          <div className="mt-3 flex gap-2">
            <NavLink to="/account" className="btn-secondary flex-1 px-2 py-1.5 text-xs">
              Your account
            </NavLink>
            <button
              type="button"
              className="btn-secondary flex-1 px-2 py-1.5 text-xs"
              onClick={signOut}
            >
              <LogOut className="h-3.5 w-3.5" aria-hidden="true" />
              Sign out
            </button>
          </div>
          {can('dashboard:read') ? (
            <div className="mt-3">
              <GlobalSearch />
            </div>
          ) : null}
        </div>
      ) : null}

      {/*
        In the drawer, menu and footer scroll together as one column, so a tall
        footer (stats, company, vertical) never squeezes the menu.
      */}
      <div className={drawer ? 'scrollbar-none flex-1 overflow-y-auto' : 'contents'}>
        <nav
          className={`scrollbar-none pb-4 ${drawer ? '' : 'flex-1 overflow-y-auto'} ${rail ? 'px-2' : 'px-3'}`}
        >
          {visibleGroups.map((group) => (
            <div key={group.title ?? 'root'} className="mb-5">
              {group.title ? (
                rail ? (
                  <hr className="mx-2 mb-2 border-ink-100" />
                ) : (
                  <p className="nav-group-title">{group.title}</p>
                )
              ) : null}
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/payments'}
                  title={rail ? item.label : undefined}
                  className={({ isActive }) =>
                    `nav-item ${rail ? 'justify-center px-0' : ''} ${
                      isActive ? 'nav-item-active' : ''
                    }`
                  }
                >
                  <item.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                  {rail ? <span className="sr-only">{item.label}</span> : item.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className={`shrink-0 border-t border-ink-100 ${rail ? 'px-2 py-3' : 'px-3 py-4'}`}>
          {/* Quick stats are the read-out, not the navigation, so they follow it. */}
          {summary && !rail ? (
            <div>
              <p className="nav-group-title px-0">Quick stats</p>
              <div className="space-y-3 pt-1">
                <Meter
                  label="Awaiting approval"
                  amount={summary.invoices.pendingApprovalAmount}
                  total={summary.totalPayables}
                  className="bg-brand-600"
                />
                <Meter
                  label="Ready to pay"
                  amount={summary.payments.readyForPayment}
                  total={summary.totalPayables}
                  className="bg-peridot-500"
                />
                <Meter
                  label="Overdue"
                  amount={summary.invoices.overdueAmount}
                  total={summary.totalPayables}
                  className="bg-amber-500"
                />
              </div>
              {summary.payrollHidden ? (
                <p className="mt-2 text-[11px] text-ink-400">Excludes payroll.</p>
              ) : null}
            </div>
          ) : null}

          {/* The company switcher anchors the very bottom — it scopes everything above it. */}
          {switcher ? (
            rail ? (
              <button
                type="button"
                className="nav-item w-full justify-center px-0"
                onClick={toggleCollapsed}
                title={switcher.find((company) => company.id === companyId)?.name ?? 'Company'}
              >
                <Building2 className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span className="sr-only">Change company</span>
              </button>
            ) : (
              <div className={summary ? 'mt-4' : ''}>
                <CompanySwitcher
                  companies={switcher}
                  value={companyId ?? null}
                  onChange={setCompanyId}
                />
              </div>
            )
          ) : null}

          {/*
          The vertical lens, directly under the company it belongs to. A view
          filter only — the server already refuses a vertical this user was not
          granted, so this narrows what they see rather than what they may see.
        */}
          {verticalChoices && !rail ? (
            <label className="mt-2 block">
              <span className="sr-only">Vertical</span>
              <select
                className="input text-sm"
                value={verticalId ?? ''}
                onChange={(event) => setVerticalId(event.target.value || undefined)}
              >
                <option value="">All verticals</option>
                {verticalChoices.map((vertical) => (
                  <option key={vertical.id} value={vertical.id}>
                    {vertical.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          {/*
          The common "View in" choice: every amount in the company's own
          currency, or as rupees at its reference rate. Only offered where
          there is something to convert.
        */}
          {company.currency !== 'INR' && !rail ? (
            <label className="mt-2 block">
              <span className="sr-only">View amounts in</span>
              <select
                className="input text-sm"
                value={viewIn}
                onChange={(event) => setViewIn(event.target.value === 'INR' ? 'INR' : 'NATIVE')}
              >
                <option value="NATIVE">View in {company.currency}</option>
                <option value="INR">View in INR</option>
              </select>
              {/* The rate is fetched live when INR is chosen; say where it came from. */}
              {viewIn === 'INR' ? (
                <span className="mt-1 block text-xs text-ink-500">
                  {inrRates?.rates[company.currency]
                    ? `1 ${company.currency} ≈ ₹${inrRates.rates[company.currency]!.toFixed(2)} · ${inrRates.source}, ${inrRates.asOf}${inrRates.stale ? ' (last known)' : ''}`
                    : 'Fetching the latest rate…'}
                </span>
              ) : null}
            </label>
          ) : null}
        </div>
      </div>
    </>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-ink-50">
      <aside
        className={`hidden shrink-0 flex-col border-r border-ink-200 bg-white transition-[width] duration-200 lg:flex ${
          collapsed ? 'w-14' : 'w-56'
        }`}
      >
        {renderSidebar(collapsed)}
      </aside>

      {/* Below `lg` the sidebar is the only navigation there is, so it becomes a drawer. */}
      {navOpen ? (
        <div
          className="scrim z-40 lg:hidden"
          onClick={() => setNavOpen(false)}
          aria-hidden="true"
        />
      ) : null}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
        aria-hidden={!navOpen}
        className={`fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-ink-200 bg-white transition-transform duration-200 lg:hidden ${
          navOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {renderSidebar(false, true)}
      </aside>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <DueReminderBanner />
        {compact ? (
          // Phone and tablet: menu, greeting, notifications — the rest is in the drawer.
          <header className="flex shrink-0 items-center gap-3 border-b border-ink-200 bg-white px-4 py-3">
            <button
              className="btn-icon shrink-0"
              onClick={() => setNavOpen(true)}
              aria-label="Open navigation"
            >
              <Menu className="h-5 w-5" aria-hidden="true" />
            </button>
            <div className="min-w-0 flex-1">
              <p className="truncate text-base font-semibold text-ink-900">
                Welcome, {firstName(user?.name)}
              </p>
              <p className="truncate text-xs text-ink-500">
                Here&rsquo;s where the money is, right now
              </p>
            </div>
            {notificationsLink}
          </header>
        ) : (
          <header className="flex shrink-0 flex-wrap items-center gap-3 border-b border-ink-200 bg-white px-4 py-3 sm:px-6">
            {/* Left and right flanks share a basis of 0, so the palette trigger
              between them lands on the centre line rather than wherever the
              greeting happens to end. */}
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <button
                className="btn-icon shrink-0 lg:hidden"
                onClick={() => setNavOpen(true)}
                aria-label="Open navigation"
              >
                <Menu className="h-5 w-5" aria-hidden="true" />
              </button>

              <div className="hidden min-w-0 md:block">
                <p className="truncate text-base font-semibold text-ink-900">
                  Welcome back, {firstName(user?.name)}
                </p>
                <p className="truncate text-xs text-ink-500">
                  Here is where the money stands today.
                </p>
              </div>
            </div>

            {/* Record search is a dashboard read; a vendor login has nothing to search. */}
            {can('dashboard:read') ? (
              <div className="order-last w-full min-w-0 sm:order-none sm:w-auto sm:max-w-md sm:flex-1">
                <GlobalSearch />
              </div>
            ) : null}

            {/* Sized to its contents: bell, Upload and the profile never run under the search. */}
            <div className="flex shrink-0 items-center justify-end gap-3">
              {notificationsLink}

              {can('invoice:create') ? (
                <button
                  type="button"
                  onClick={() => setUploadOpen(true)}
                  className="btn-primary hidden shrink-0 sm:inline-flex"
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  Upload invoice
                </button>
              ) : null}

              <div className="relative shrink-0" ref={menuRef}>
                <button
                  className="flex items-center gap-2 rounded-full border border-ink-200 bg-white py-1 pl-1 pr-2 text-sm transition-colors hover:border-ink-300 hover:bg-ink-50 xl:pr-3"
                  onClick={() => setMenuOpen((open) => !open)}
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-100 text-xs font-semibold text-brand-800">
                    {initials(user?.name)}
                  </span>
                  <span className="hidden text-left xl:block">
                    <span className="block font-medium leading-tight text-ink-900">
                      {user?.name}
                    </span>
                    <span className="block text-xs leading-tight text-ink-500">
                      {user ? (ROLE_LABELS[user.roleKey as RoleKey] ?? user.roleKey) : null}
                    </span>
                  </span>
                </button>

                {menuOpen ? (
                  <div className="menu absolute right-0 z-20 mt-1 w-48">
                    <NavLink to="/account" className="menu-item" onClick={() => setMenuOpen(false)}>
                      Your account
                    </NavLink>
                    <button
                      className="menu-item flex items-center gap-2"
                      onClick={() => {
                        setMenuOpen(false);
                        signOut();
                      }}
                    >
                      <LogOut className="h-4 w-4" aria-hidden="true" />
                      Sign out
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </header>
        )}

        {/* Room at the foot for the bottom bar, so the last card is not under it. */}
        <main
          className={`flex-1 overflow-y-auto p-4 sm:p-6 ${showBottomNav ? 'pb-36 sm:pb-36' : compact ? 'pb-24' : ''}`}
        >
          <Outlet />
        </main>
      </div>

      {showBottomNav ? (
        <nav
          aria-label="Primary"
          className="fixed inset-x-0 bottom-0 z-30 border-t border-ink-200 bg-white pb-[env(safe-area-inset-bottom)]"
        >
          <ul className="flex">
            {bottomItems.map((item) => (
              <li key={item.to} className="flex-1">
                <NavLink
                  to={item.to}
                  end={item.to === '/payments'}
                  className={({ isActive }) =>
                    `relative flex flex-col items-center gap-1 py-2 text-[11px] font-medium ${
                      isActive ? 'text-brand-700' : 'text-ink-500 hover:text-ink-900'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {isActive ? (
                        <span
                          className="absolute inset-x-6 top-0 h-0.5 rounded-full bg-brand-600"
                          aria-hidden="true"
                        />
                      ) : null}
                      <item.icon className="h-5 w-5" aria-hidden="true" />
                      {item.short}
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      ) : null}

      {/* Upload, one tap away above the bottom bar. */}
      {compact && can('invoice:create') ? (
        <button
          type="button"
          onClick={() => setUploadOpen(true)}
          className={`fixed right-4 z-30 flex h-14 w-14 items-center justify-center rounded-full bg-brand-700 text-white shadow-lg hover:bg-brand-800 ${
            showBottomNav ? 'bottom-20' : 'bottom-6'
          }`}
          aria-label="Upload invoice"
          title="Upload invoice"
        >
          <Plus className="h-6 w-6" aria-hidden="true" />
        </button>
      ) : null}

      {uploadOpen ? <UploadInvoiceModal onClose={() => setUploadOpen(false)} /> : null}
    </div>
  );
}

/**
 * One quick-stat bar.
 *
 * Every bar shares `totalPayables` as its denominator so the three are honestly
 * comparable; with nothing outstanding there is no proportion to draw.
 */
function Meter({
  label,
  amount,
  total,
  className,
}: {
  label: string;
  amount: number;
  total: number;
  className: string;
}) {
  const money = useMoney();
  if (!total) return null;
  const percent = Math.min(100, Math.max(0, (amount / total) * 100));

  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <span className="text-xs font-medium text-ink-600">{label}</span>
        <span className="tabular text-xs font-semibold text-ink-900">{money.compact(amount)}</span>
      </div>
      <div className="meter">
        <div className={`meter-bar ${className}`} style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}

function firstName(name: string | undefined): string {
  if (!name) return 'there';
  return name.split(' ')[0] ?? name;
}

function initials(name: string | undefined): string {
  if (!name) return '?';
  return name
    .split(' ')
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}
